/**
 * Thin fetch wrapper for backend API calls.
 *
 * - Sends the session cookie via credentials:'include'.
 * - On 401, redirects the browser to /auth/login (the backend handles the
 *   OIDC roundtrip; we just bounce off it).
 * - All other non-2xx statuses throw with the status text.
 *
 * Use alongside the generated types from `src/api/generated/api.ts`
 * (regenerate via `just gen-client`).
 */
export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, headers, ...rest } = opts;
  const resp = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(headers ?? {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...rest,
  });

  if (resp.status === 401) {
    window.location.href = '/auth/login';
    throw new ApiError('unauthenticated', 401);
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new ApiError(text || resp.statusText, resp.status);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
